import type { ListWishlistsQuery } from '@nexus/api-types';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type {
  CancelWishlistReservationByManagerRequest,
  CancelWishlistReservationResponse,
  ConfirmWishlistReservationRequest,
  CreateWishlistItemRequest,
  CreateWishlistRequest,
  CreateWishlistShareLinkRequest,
  CreateWishlistShareLinkResponse,
  DeleteWishlistItemResponse,
  DisableWishlistShareLinkResponse,
  GuestReservationConfirmedResponse,
  GuestReservationRequest,
  GuestReservationStartedResponse,
  InviteWishlistGuestRequest,
  InviteWishlistGuestResponse,
  ListWishlistInvitationsResponse,
  ListWishlistsOverviewResponse,
  PublicWishlist,
  PublicWishlistResolveRequest,
  RemoveWishlistGuestResponse,
  RotateWishlistShareLinkResponse,
  UpdateWishlistAccessPolicyRequest,
  UpdateWishlistAccessPolicyResponse,
  UpdateWishlistItemRequest,
  UpdateWishlistRequest,
  WishlistAccessPolicyResponse,
  WishlistManagerDetail,
} from '@nexus/api-types';
import type { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WishlistApiClientService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly basePath: string = '/api/v1/wishlists';
  private readonly publicLinkPath: string = '/api/v1/public/wishlist-links';
  private readonly publicReservationPath: string = '/api/v1/public/wishlist-reservations';

  public listWishlistsOverview(query: ListWishlistsQuery = {}): Observable<ListWishlistsOverviewResponse> {
    return this.httpClient.get<ListWishlistsOverviewResponse>(this.basePath, {
      params: query.beneficiaryProfileId === undefined ? {} : { beneficiaryProfileId: query.beneficiaryProfileId },
    });
  }

  public createWishlist(request: CreateWishlistRequest): Observable<WishlistManagerDetail> {
    return this.httpClient.post<WishlistManagerDetail>(this.basePath, request);
  }

  public getWishlistDetail(wishlistId: string): Observable<WishlistManagerDetail> {
    return this.httpClient.get<WishlistManagerDetail>(this.createWishlistPath(wishlistId));
  }

  public updateWishlist(wishlistId: string, request: UpdateWishlistRequest): Observable<WishlistManagerDetail> {
    return this.httpClient.patch<WishlistManagerDetail>(this.createWishlistPath(wishlistId), request);
  }

  public archiveWishlist(wishlistId: string): Observable<WishlistManagerDetail> {
    return this.httpClient.post<WishlistManagerDetail>(`${this.createWishlistPath(wishlistId)}/archive`, {});
  }

  public addWishlistItem(
    wishlistId: string,
    request: CreateWishlistItemRequest,
  ): Observable<WishlistManagerDetail> {
    return this.httpClient.post<WishlistManagerDetail>(`${this.createWishlistPath(wishlistId)}/items`, request);
  }

  public updateWishlistItem(
    wishlistId: string,
    itemId: string,
    request: UpdateWishlistItemRequest,
  ): Observable<WishlistManagerDetail> {
    return this.httpClient.patch<WishlistManagerDetail>(
      `${this.createWishlistPath(wishlistId)}/items/${this.encodePathSegment(itemId)}`,
      request,
    );
  }

  public deleteWishlistItem(wishlistId: string, itemId: string): Observable<DeleteWishlistItemResponse> {
    return this.httpClient.delete<DeleteWishlistItemResponse>(
      `${this.createWishlistPath(wishlistId)}/items/${this.encodePathSegment(itemId)}`,
    );
  }

  public getAccessPolicy(wishlistId: string): Observable<WishlistAccessPolicyResponse> {
    return this.httpClient.get<WishlistAccessPolicyResponse>(`${this.createWishlistPath(wishlistId)}/access-policy`);
  }

  public updateAccessPolicy(
    wishlistId: string,
    request: UpdateWishlistAccessPolicyRequest,
  ): Observable<UpdateWishlistAccessPolicyResponse> {
    return this.httpClient.patch<UpdateWishlistAccessPolicyResponse>(
      `${this.createWishlistPath(wishlistId)}/access-policy`,
      request,
    );
  }

  public createShareLink(
    wishlistId: string,
    request: CreateWishlistShareLinkRequest,
  ): Observable<CreateWishlistShareLinkResponse> {
    return this.httpClient.post<CreateWishlistShareLinkResponse>(
      `${this.createWishlistPath(wishlistId)}/share-link`,
      request,
    );
  }

  public rotateShareLink(wishlistId: string): Observable<RotateWishlistShareLinkResponse> {
    return this.httpClient.post<RotateWishlistShareLinkResponse>(
      `${this.createWishlistPath(wishlistId)}/share-link/rotate`,
      {},
    );
  }

  public disableShareLink(wishlistId: string): Observable<DisableWishlistShareLinkResponse> {
    return this.httpClient.post<DisableWishlistShareLinkResponse>(
      `${this.createWishlistPath(wishlistId)}/share-link/disable`,
      {},
    );
  }

  public listInvitedGuests(wishlistId: string): Observable<ListWishlistInvitationsResponse> {
    return this.httpClient.get<ListWishlistInvitationsResponse>(`${this.createWishlistPath(wishlistId)}/invitations`);
  }

  public inviteGuest(wishlistId: string, request: InviteWishlistGuestRequest): Observable<InviteWishlistGuestResponse> {
    return this.httpClient.post<InviteWishlistGuestResponse>(
      `${this.createWishlistPath(wishlistId)}/invitations`,
      request,
    );
  }

  public removeInvitedGuest(wishlistId: string, invitationId: string): Observable<RemoveWishlistGuestResponse> {
    return this.httpClient.delete<RemoveWishlistGuestResponse>(
      `${this.createWishlistPath(wishlistId)}/invitations/${this.encodePathSegment(invitationId)}`,
    );
  }

  public cancelReservationByManager(
    wishlistId: string,
    reservationId: string,
    request: CancelWishlistReservationByManagerRequest,
  ): Observable<CancelWishlistReservationResponse> {
    return this.httpClient.post<CancelWishlistReservationResponse>(
      `${this.createWishlistPath(wishlistId)}/reservations/${this.encodePathSegment(reservationId)}/cancel`,
      request,
    );
  }

  public resolvePublicWishlist(request: PublicWishlistResolveRequest): Observable<PublicWishlist> {
    return this.httpClient.post<PublicWishlist>(`${this.publicLinkPath}/resolve`, request);
  }

  public createReservation(request: GuestReservationRequest): Observable<GuestReservationStartedResponse> {
    return this.httpClient.post<GuestReservationStartedResponse>(this.publicReservationPath, request);
  }

  public reservationStatus(request: GuestReservationRequest): Observable<GuestReservationStartedResponse> {
    return this.httpClient.post<GuestReservationStartedResponse>(`${this.publicReservationPath}/status`, request);
  }

  public resendReservation(request: GuestReservationRequest): Observable<GuestReservationStartedResponse> {
    return this.httpClient.post<GuestReservationStartedResponse>(`${this.publicReservationPath}/resend`, request);
  }

  public confirmReservation(
    request: ConfirmWishlistReservationRequest,
  ): Observable<GuestReservationConfirmedResponse> {
    return this.httpClient.post<GuestReservationConfirmedResponse>(`${this.publicReservationPath}/confirm`, request);
  }

  private createWishlistPath(wishlistId: string): string {
    return `${this.basePath}/${this.encodePathSegment(wishlistId)}`;
  }

  private encodePathSegment(value: string): string {
    return encodeURIComponent(value);
  }
}
